export const wf = {
  zoomProjectId: null,
  zoomPhaseId: null,
  zoomMsId: null,
  zoomCat: null,
  collapsed: {},
  menuNodeId: null,
  panels: {},
  dirty: {},
  selectMode: null,
  searchQuery: '',
  _searchTimer: null,
  zoomItemId: null,
  zoomSection: null,
  zoomConceptId: null,
  zoomConceptDetailId: null,
  zoomRoot: null,
  suppressAnim: false,
  selected: [],
  editingLft: null,
  hideDone: false,
  hideDoneKeys: {},
  allCollapsed: false,
  // Set by resolveCurrentMember() in initOutline — Airtable record ID of the
  // logged-in member. Not persisted to localStorage (derived from auth session).
  currentMemberId: null,
  // Slash command bar — ephemeral, never persisted to localStorage
  commandOpen: false,
  commandQuery: '',
  commandHistory: [],
  // Outline filter set by slash commands — cleared when bar closes.
  // Shape when active: { type: 'msIds', msIds: Set<string> }
  cmdFilter: null,
  // Keyboard navigation cursor. kbKey is the STABLE identity of the selected row (its node's
  // data-key) — it survives DOM rebuilds (React re-render / lazy load) so arrow nav doesn't drift.
  // kbIdx is a fallback position for the rare keyless row. Neither is persisted to localStorage.
  kbIdx: -1,
  kbKey: null,
};

const WF_STORE_VERSION = 2;
// Two stores so the team can "split screen" without tabs fighting over state:
//  • sessionStorage (per tab/window) holds THIS tab's live navigation — independent across tabs.
//  • localStorage (shared) holds the last-session snapshot so a fresh tab / a return visit restores
//    where you left off. A new tab seeds its session state from this snapshot on first load.
const GLOBAL_STORE_KEY = 'aj-outline-v2-global';   // localStorage — last-session snapshot (shared)
const TAB_STORE_KEY    = 'aj-outline-v2-tab';      // sessionStorage — this tab's live state

// CT-024 split-screen: the lower pane loads the app in a same-origin iframe with ?split=1. A
// same-origin iframe SHARES sessionStorage (and localStorage) with its parent, so if the embedded
// copy read or wrote the outline snapshot the two panes would clobber each other's restore — and a
// deep-rooted pane would poison the next fresh load. In embed mode persistence is a no-op; the pane's
// view comes solely from the ?z= deep link applied in initOutline.
const EMBED = (function() {
  try { return new URLSearchParams(window.location.search).get('split') === '1'; } catch (e) { return false; }
})();
export function isEmbedded() { return EMBED; }

export function initViewState() {
  /* No-op — key is now global, not per-project */
}

function viewSnapshot() {
  return {
    v: WF_STORE_VERSION,
    collapsed: wf.collapsed,
    hideDone: wf.hideDone,
    hideDoneKeys: wf.hideDoneKeys,
    zoomProjectId: wf.zoomProjectId,
    zoomPhaseId: wf.zoomPhaseId,
    zoomMsId: wf.zoomMsId,
    zoomCat: wf.zoomCat,
    zoomItemId: wf.zoomItemId,
    zoomRoot: wf.zoomRoot,
    zoomSection: wf.zoomSection,
    zoomConceptId: wf.zoomConceptId
  };
}

export function saveViewState() {
  if (EMBED) return;   // embedded split pane never writes the shared snapshot (see EMBED note above)
  try {
    const json = JSON.stringify(viewSnapshot());
    try { sessionStorage.setItem(TAB_STORE_KEY, json); } catch(e) {}   // per-tab live state
    localStorage.setItem(GLOBAL_STORE_KEY, json);                       // shared last-session snapshot
  } catch(e) {}
}

/**
 * Returns true if this milestone should be hidden based on the current hide-done filter.
 * Checks in priority order: milestone-specific → phase-specific → global.
 */
export function shouldHideDone(msId, phId) {
  if (!wf.hideDoneKeys) return false
  return !!(wf.hideDoneKeys['global'] || (phId && wf.hideDoneKeys['ph:' + phId]) || wf.hideDoneKeys['ms:' + msId])
}

function applyViewSnapshot(snap) {
  if (!snap || snap.v !== WF_STORE_VERSION || typeof snap.collapsed !== 'object') return false;
  wf.collapsed     = snap.collapsed    || {};
  wf.hideDone      = !!snap.hideDone;
  wf.hideDoneKeys  = snap.hideDoneKeys || {};
  wf.zoomProjectId = snap.zoomProjectId || null;
  wf.zoomPhaseId   = snap.zoomPhaseId  || null;
  wf.zoomMsId      = snap.zoomMsId     || null;
  wf.zoomCat       = snap.zoomCat      || null;
  wf.zoomItemId    = snap.zoomItemId   || null;
  wf.zoomRoot      = snap.zoomRoot     || null;
  wf.zoomSection   = snap.zoomSection  || null;
  wf.zoomConceptId = snap.zoomConceptId || null;
  return true;
}

export function loadViewState() {
  if (EMBED) return false;   // embedded split pane starts clean; ?z= alone sets its view
  // Prefer THIS tab's own state (sessionStorage) so split-screen tabs stay independent.
  try {
    const tabRaw = sessionStorage.getItem(TAB_STORE_KEY);
    if (tabRaw && applyViewSnapshot(JSON.parse(tabRaw))) return true;
  } catch(e) {}
  // Fall back to the shared last-session snapshot (fresh tab / return visit), then seed this tab.
  try {
    const raw = localStorage.getItem(GLOBAL_STORE_KEY);
    if (!raw) return false;
    if (!applyViewSnapshot(JSON.parse(raw))) return false;
    try { sessionStorage.setItem(TAB_STORE_KEY, raw); } catch(e2) {}
    return true;
  } catch(e) {
    try { localStorage.removeItem(GLOBAL_STORE_KEY); } catch(e2) {}
    return false;
  }
}
